import {
  BarChart3,
  BookCopy,
  Boxes,
  Building2,
  ClipboardCheck,
  DollarSign,
  FileCheck2,
  Handshake,
  Library,
  Megaphone,
  PackageCheck,
  PenTool,
  ReceiptText,
  ShieldCheck,
  ShoppingBag,
  Truck
} from "lucide-react";

export type BookFormat = "Kindle" | "Paperback" | "Hardcover" | "Large print" | "Signed";

export type Author = {
  slug: string;
  name: string;
  role: string;
  photo: string;
  shortBio: string;
  longBio: string;
  interviewTopics: string[];
  pressKit: string;
};

export type Book = {
  slug: string;
  title: string;
  subtitle: string;
  hook: string;
  promise: string;
  authorSlug: string;
  seriesSlug: string;
  seriesOrder: number;
  coverTone: string;
  description: string;
  formats: BookFormat[];
  isbn: Record<string, string>;
  asin: string;
  ingramTitleId: string;
  categories: string[];
  keywords: string[];
  trimSize: string;
  pageCount: number;
  publicationDate: string;
  listPrices: Record<string, string>;
  buyLinks: { label: string; href: string; channel: string }[];
};

export type Series = {
  slug: string;
  title: string;
  authorSlug: string;
  description: string;
  promise: string;
};

export type PublishingPackage = {
  slug: string;
  name: string;
  price: string;
  audience: string;
  summary: string;
  includes: string[];
  bestFor: string;
};

export const authors: Author[] = [
  {
    slug: "johna-walsh",
    name: "Johna Walsh",
    role: "Castlehale author",
    photo: "/assets/castlehale-hero.png",
    shortBio:
      "Johna Walsh writes character-driven fiction with hard choices, spare pressure, and a strong sense of place.",
    longBio:
      "Her Castlehale page gathers approved biography, books, series, public materials, and reader-facing purchase paths in one place.",
    interviewTopics: [
      "Fiction with place and consequence",
      "Building a long-running body of work",
      "Writing for loyal readers",
      "Working with an independent publisher"
    ],
    pressKit: "/authors/johna-walsh"
  }
];

export const series: Series[] = [
  {
    slug: "hieronymous-stanton",
    title: "Hieronymous Stanton",
    authorSlug: "johna-walsh",
    description:
      "The public series record for Johna Walsh's Hieronymous Stanton fiction.",
    promise:
      "A clean reading path, publisher-managed editions, and direct reader support."
  }
];

const catalogBuyLinks = [
  { label: "Signed copy", href: "/shop", channel: "Castlehale direct" },
  { label: "Ebook", href: "/shop", channel: "Retail link forthcoming" },
  { label: "Library request", href: "/libraries", channel: "Library acquisition" }
];

export const books: Book[] = [
  {
    slug: "invictus-hieronymous-stanton",
    title: "Invictus Hieronymous Stanton",
    subtitle: "The Trouble With Cowboys",
    hook: "A Hieronymous Stanton Western from Johna Walsh.",
    promise: "A professionally published Castlehale title with direct ordering and library support.",
    authorSlug: "johna-walsh",
    seriesSlug: "hieronymous-stanton",
    seriesOrder: 1,
    coverTone: "rust",
    description:
      "Invictus Hieronymous Stanton: The Trouble With Cowboys is the first Hieronymous Stanton title in the Castlehale catalog.",
    formats: ["Kindle", "Paperback", "Signed"],
    isbn: { paperback: "978-1-959999-01-0", ebook: "978-1-959999-02-7" },
    asin: "ASIN-PENDING-001",
    ingramTitleId: "INGRAM-PENDING-001",
    categories: ["FIC000000 Fiction / General"],
    keywords: ["Johna Walsh", "Hieronymous Stanton", "western fiction", "direct from publisher"],
    trimSize: "5.5 x 8.5 in",
    pageCount: 312,
    publicationDate: "2026-10-06",
    listPrices: { Kindle: "$5.99", Paperback: "$16.99", Signed: "$24.00" },
    buyLinks: catalogBuyLinks
  }
];

export const publishingPackages: PublishingPackage[] = [
  {
    slug: "launch-file-prep",
    name: "Launch File Prep",
    price: "$950-$1,500",
    audience: "For authors who already have editing and cover art.",
    summary: "Clean, upload-ready book files and retailer-page checks for authors preparing to publish.",
    includes: [
      "Manuscript cleanup review",
      "Kindle-ready file",
      "Paperback-ready PDF",
      "Retailer upload checklist",
      "Book description and category review",
      "One revision pass",
      "Deliverables summary"
    ],
    bestFor: "Finished manuscripts with production pieces already in hand."
  },
  {
    slug: "complete-publish-ready-setup",
    name: "Complete Publish-Ready Setup",
    price: "$1,950-$2,750",
    audience: "For finished manuscripts that need professional launch files and platform setup.",
    summary: "Formatting, retailer copy, channel support, direct-sales page, and author portal setup.",
    includes: [
      "Kindle and paperback formatting",
      "Cover wrap preparation from supplied art",
      "Barcode and ISBN placement guidance",
      "Retailer upload support",
      "Distributor setup guidance",
      "Book description and category package",
      "Keyword and audience positioning",
      "Sell sheet and basic press kit",
      "Direct-sales product page",
      "Author portal setup"
    ],
    bestFor: "Authors who want the launch plumbing handled clearly."
  },
  {
    slug: "series-launch-machine",
    name: "Series Launch Machine",
    price: "$3,500-$4,250",
    audience: "For authors with 3+ related books or a serious catalog.",
    summary: "A repeatable publishing system for series pages, bundles, reporting, and launch rhythm.",
    includes: [
      "Everything in Complete Publish-Ready Setup",
      "Series branding system",
      "Multi-book positioning map",
      "Author and series landing pages",
      "Direct-sales bundle setup",
      "Review-copy tracker",
      "Library ordering kit",
      "Launch calendar",
      "Sales reporting setup",
      "30-day post-launch reporting review"
    ],
    bestFor: "Series authors who care about read-through, reporting, and backlist."
  },
  {
    slug: "castlehale-select",
    name: "Castlehale Select Publishing",
    price: "By invitation / contract",
    audience: "For manuscripts Castlehale chooses to publish under the Castlehale Press imprint.",
    summary: "Selective imprint publication with explicit rights, royalties, channels, and reporting.",
    includes: [
      "Castlehale imprint publication",
      "Production management",
      "ISBN/imprint handling",
      "Sales channel management",
      "Royalty reporting",
      "Author portal",
      "Rights tracking",
      "Marketing coordination"
    ],
    bestFor: "Projects accepted through a written publishing agreement."
  }
];

export const alaCarteServices = [
  "Kindle formatting",
  "Paperback formatting",
  "Hardcover formatting",
  "Cover wrap layout",
  "Book description and category package",
  "Retailer upload support",
  "Distributor setup support",
  "Press kit",
  "Sell sheet",
  "Author website/page",
  "Direct-sales page",
  "Stripe/PayPal product setup",
  "Sales reporting setup",
  "CSV import/report setup",
  "Library outreach kit",
  "Series bible/publishing map"
];

export const operations = [
  { title: "Catalog", icon: BookCopy, copy: "Books, authors, series, editions, identifiers, categories, and publication states kept in order." },
  { title: "Direct Readers", icon: ShoppingBag, copy: "Signed copies, bundles, book club packs, library sponsor packs, and event sales handled with care." },
  { title: "Reporting", icon: BarChart3, copy: "Sales from major channels and direct orders gathered into clear author-facing summaries." },
  { title: "Fulfillment", icon: ReceiptText, copy: "Shipping, tax tracking, packaging costs, carrier notes, and fulfillment status recorded cleanly." },
  { title: "Inventory", icon: Boxes, copy: "Proofs, author copies, signed stock, review copies, library copies, event stock, returns, and damage." },
  { title: "Rights", icon: ShieldCheck, copy: "Copyright owner, format, territory, term, offers, contacts, and written rights language kept visible." },
  { title: "Press", icon: Library, copy: "Publisher notes, sell sheets, library sheets, review-copy records, and approved press materials." },
  { title: "Author Visibility", icon: ClipboardCheck, copy: "Private author views for books, assets, reports, statements, project tasks, and launch notes." }
];

export const servicePillars = [
  { title: "Manuscript production", icon: PenTool, items: ["DOCX cleanup", "Kindle formatting", "Paperback PDF", "Hardcover files", "Front/back matter", "Proofing workflow"] },
  { title: "Cover and brand system", icon: FileCheck2, items: ["Front cover", "Paperback wrap", "Hardcover wrap", "Spine/barcode placement", "Series visual system", "Launch graphics"] },
  { title: "Retailer copy and ISBN support", icon: PackageCheck, items: ["Category guidance", "Keyword positioning", "Back-cover copy", "ISBN checklist", "Format mapping", "Identifier tracking"] },
  { title: "Channel setup", icon: Building2, items: ["Ebook setup", "Paperback setup", "Hardcover setup", "Distributor guidance", "Direct product pages", "Stripe/PayPal links"] },
  { title: "Library readiness", icon: Library, items: ["Sell sheets", "Title details", "Distributor language", "Book club assets", "Review-copy tracker", "Local-author kit"] },
  { title: "Reporting", icon: DollarSign, items: ["Sales by book", "Sales by channel", "Profit estimates", "Tax and shipping", "Inventory", "Read-through estimates"] },
  { title: "Launch system", icon: Megaphone, items: ["Launch calendar", "Hook bank", "Email/social copy", "Press kit", "Ad-test tracker", "Campaign reporting"] },
  { title: "Fulfillment", icon: Truck, items: ["Signed-copy flow", "Bundle offers", "Shipping records", "Private sales", "Event stock", "Handoff checklist"] }
];

export const comparisonRows = [
  ["Vague marketing package", "Named deliverables, finished files, product pages, reports, and handoff items"],
  ["Broad promotion claims", "Sales setup: retailer guidance, direct checkout links, reporting, and follow-up"],
  ["Mystery distribution", "Clear channel setup: retailer pages, distributor guidance, direct-sales pages, and library-ready language"],
  ["Author left alone after upload", "Author portal with assets, sales summaries, statements, and launch tasks"],
  ["Unclear rights", "Rights map with copyright, imprint, format, territory, term, and contract status"],
  ["One-book upload", "Series and catalog strategy with read-through, bundles, consistent copy, and backlist planning"],
  ["Generic press release", "Usable sell sheets, press kit pages, library/book club sheets, and review-copy workflow"],
  ["Inflated package price", "Transparent packages plus a la carte services and quote builder"],
  ["No sales tracking", "Imported retailer, distributor, and direct-sales summaries with normalized CSV export"]
];

export const businessCopy = {
  value:
    "Castlehale prepares books for publication with finished files, coherent editions, sales channels, reporting, direct sales, library materials, and a practical launch plan.",
  compareHeadline: "Know what your publishing package includes.",
  compare:
    "Castlehale Press packages name the deliverables: clean files, clear editions, professional covers, direct-sales setup, library-ready materials, channel tracking, inventory, tax and shipping records, and author reporting.",
  rightsService:
    "For author-services clients, the author keeps copyright. Castlehale performs contracted production and publishing support. Files, deliverables, ISBN/imprint use, and handoff terms are defined by written agreement. Service work is distinct from Castlehale Select Publishing.",
  rightsSelect:
    "For Castlehale Select, rights are handled by written publishing agreement. Royalty rate, territory, format, duration, subsidiary rights, and reversion language must be explicit."
};

export const noPromises = [
  "We do not guarantee bestseller status.",
  "We do not sell fake reviews.",
  "We do not promise bookstore stocking.",
  "We do not hide deliverables inside vague package language.",
  "We do not require authors to surrender copyright for service work.",
  "We do not run ad spend without a measurable plan."
];

export const inquiryChoices = {
  manuscriptStatus: ["Draft complete", "Revising", "Edited", "Proofread", "Not finished yet"],
  genre: ["Fiction", "Mystery", "Romance", "Historical", "Memoir", "Business", "Nonfiction", "Other"],
  editingStatus: ["Professional edit complete", "Self-edited", "In editing", "Needs editing quote", "Not sure"],
  coverStatus: ["Final cover art ready", "Concept only", "Needs cover wrap", "Needs full cover design", "Not sure"],
  isbnStatus: ["I own ISBNs", "Need ISBN guidance", "Use platform identifiers only", "Not sure"],
  budgetRange: ["Under $1,000", "$1,000-$2,500", "$2,500-$4,500", "$4,500+", "Need scoped quote"],
  timeline: ["30 days", "60-90 days", "3-6 months", "Flexible", "Catalog/series plan"]
};

export function getAuthor(slug: string) {
  return authors.find((author) => author.slug === slug);
}

export function getBook(slug: string) {
  return books.find((book) => book.slug === slug);
}

export function getSeries(slug: string) {
  return series.find((item) => item.slug === slug);
}

export function booksByAuthor(slug: string) {
  return books.filter((book) => book.authorSlug === slug).sort((a, b) => a.seriesOrder - b.seriesOrder);
}

export function booksBySeries(slug: string) {
  return books.filter((book) => book.seriesSlug === slug).sort((a, b) => a.seriesOrder - b.seriesOrder);
}
